/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Toast from 'react-native-toast-message';
import { View, ScrollView } from 'react-native';
import { buttonFloating } from '@/assets/styles/buttonStyles';
import { isWeb } from '@global/screens';
import { isDisableSubmit } from '@global/helpers';
import Colors from '@global/colors';
import SociollaText from '@components/sociolla/SociollaText';
import ProgressBar from '@components/global/ProgressBar';
import Button from '@components/form/Button';
import TeamMember from '@components/global/TeamMember';
import Input from '@components/form/Input';
import SelectBox from '@components/form/SelectBox';
import SalesTeamSearch from '@components/form/search/SalesTeamSearch';
import * as Layout from '@components/screen/Layout';
import { SociollaSelectBox } from '@components/sociolla/form';

import useApi from '@/hooks/useApi';
import { MsAccount } from '@api/ms-account';
import { showPopup } from '@/store/modal';

const BranchListFormScreen = (props) => {
  const dispatch = useDispatch();

  const [isEdit, setIsEdit] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [page, setPage] = useState(1);
  const [detailData, setDetailData] = useState({});
  const [teamMember, setTeamMember] = useState([]);
  const [warehouse, setWarehouse] = useState([]);

  const FormValidationSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .required('Name is required.')
      .test(
        'alphabets',
        'Branch name must contain alphabets',
        (value) => /[^0-9]/.test(value) && /[a-zA-Z]/.test(value),
      ),
    branch_code: Yup.string()
      .max(2, 'Max 2 characters.')
      .trim()
      .required('Branch code is required.')
      .test(
        'alphabets',
        'Code must contain alphabets',
        (value) => /[^0-9]/.test(value) && /[a-zA-Z]/.test(value),
      ),
    regional: Yup.object().required('Regional/Area is required.'),
    sales_team: Yup.array()
      .min(1, 'Sales team is required.')
      .required('Sales team is required.'),
    is_active: Yup.object().required('Active/In Active is required.'),
  });

  const getBranchDetail = useApi(MsAccount.getBranchDetail);

  const getDetail = async (id) => {
    const response = await getBranchDetail.request(id);
    if (response?.data?.data && Object.keys(response.data.data).length) {
      const result = response.data.data;
      result.sales_team = result.sales_team.map((item) => {
        item.id = item._id;
        return item;
      });
      result.is_active = result.is_active
        ? { label: 'Active', value: true }
        : { label: 'In Active', value: false };
      const warehouses = [];
      let teamMembersTemp = [];
      for (let i = 0; i < result.sales_team.length; i++) {
        const sales = result.sales_team[i];
        if (sales) {
          if (sales.warehouse && sales.warehouse.name) {
            if (warehouses.indexOf(sales.warehouse.name) == -1) {
              warehouses.push(sales.warehouse.name);
            }
          }
          if (sales.team_members && sales.team_members.length) {
            const tm_filtered = sales.team_members.filter(
              (m) => teamMembersTemp.findIndex((t) => t.email == m.email) == -1,
            );
            if (tm_filtered.length) {
              teamMembersTemp = teamMembersTemp.concat(
                tm_filtered.map((val) => {
                  if (sales.name) {
                    val.sales_team = sales.name;
                  }
                  return val;
                }),
              );
            }
          }
        }
      }
      if (!warehouse.length) {
        setWarehouse(warehouses);
      }
      if (!teamMember.length) {
        const tmTemp = [];
        for (let i = 0; i < teamMembersTemp.length; i++) {
          const tm = teamMembersTemp[i];
          if (tm) {
            tmTemp.push(tm);
          }
        }
        setTeamMember(tmTemp);
      }
      setDetailData(result);
    }
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setPage(1);
      setIsEdit(false);
      setIsReady(false);
      setTeamMember([]);
      setWarehouse([]);
      setDetailData({});
      if (props?.route?.params?.id) {
        setIsEdit(true);
        getDetail(props.route.params.id);
      } else {
        setIsEdit(false);
      }
      setTimeout(() => {
        setIsReady(true);
      }, 1000);
    }
    return () => {
      mounted = false;
    };
  }, [props]);

  const handleSalesTeamChange = (value, empty = false) => {
    if (empty) {
      setTeamMember([]);
      setWarehouse([]);
    } else {
      const teams = [];
      const wh = [];
      for (let i = 0; i < (value || []).length; i++) {
        const res = value[i];
        if (res.warehouse) {
          const arrwh = [res.warehouse];
          const arrwhoptional = res.optional_warehouse;
          const arrpushwh = arrwh.concat(arrwhoptional);
          if (arrwhoptional === undefined) {
            wh.push(res.warehouse);
          } else {
            arrpushwh.forEach(function (item) {
              wh.push(item);
            });
          }
        }
        if (res.team_members && res.team_members.length) {
          for (let j = 0; j < res.team_members.length; j++) {
            const team = res.team_members[j];
            if (team) {
              if (res.name) {
                team.sales_team = res.name;
              }
              teams.push(team);
            }
          }
        }
      }
      setTeamMember(teams);
      setWarehouse(wh);
    }
  };

  return isReady &&
    (isEdit
      ? !(getBranchDetail && getBranchDetail.loading) &&
        detailData &&
        Object.keys(detailData).length
      : true) ? (
    <>
      {!isWeb && <ProgressBar page={page} totalPage={2} />}
      <Layout.Container>
        <Formik
          initialValues={{
            name: isEdit ? detailData.name : '',
            branch_code: isEdit ? detailData.branch_code : '',
            regional:
              isEdit && detailData.regional
                ? detailData.regional.value
                  ? detailData.regional
                  : {
                      name: detailData.regional,
                      value: detailData.regional,
                    }
                : '',
            sales_team:
              isEdit && detailData.sales_team && detailData.sales_team.length
                ? detailData.sales_team
                : '',
            is_active: isEdit ? detailData?.is_active : '',
          }}
          validationSchema={FormValidationSchema}
          onSubmit={async (payload) => {
            dispatch(showPopup({ name: 'isLoading', value: true }));
            try {
              payload.is_active = payload.is_active.value;
              payload.regional = payload.regional ? payload.regional.value : '';
              payload.sales_team =
                payload.sales_team && payload.sales_team.length
                  ? payload.sales_team.map((item) => {
                      return { id: item.id, name: item.name };
                    })
                  : [];
              if (isEdit) {
                const updateData = await MsAccount.updateBranch(
                  props.route.params.id,
                  payload,
                );
                if (updateData && updateData.data && updateData.data.data) {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'Successfully edited data',
                    type: 'success',
                  });
                  props.navigation.navigate('BranchListDetailScreen', {
                    titleSection: detailData.name,
                    name: detailData.name,
                    id: detailData._id,
                  });
                } else {
                  Toast.show({
                    text1: 'A system error occurred',
                    type: 'error',
                  });
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                }
              } else {
                const createData = await MsAccount.createBranch(payload);
                if (createData && createData.data && createData.data.data) {
                  const detail = createData.data.data;
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'Successfully added data',
                    type: 'success',
                  });
                  if (detail && detail._id) {
                    props.navigation.navigate('BranchListDetailScreen', {
                      titleSection: detail.name,
                      name: detail.name,
                      id: detail._id,
                    });
                  } else {
                    props.navigation.navigate('BranchListScreen');
                  }
                } else {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'A system error occurred',
                    type: 'error',
                  });
                }
              }
            } catch (error) {
              dispatch(showPopup({ name: 'isLoading', value: false }));
              Toast.show({
                text1:
                  (error &&
                    error.response &&
                    error.response.data &&
                    error.response.data.message) ||
                  'A system error occurred',
                type: 'error',
              });
            }
          }}>
          {({
            handleChange,
            setFieldValue,
            validateForm,
            handleSubmit,
            errors,
            touched,
            values,
          }) => (
            <>
              {isWeb ? (
                <Layout.Wrapper white={false} hasProgressBar={true}>
                  <Layout.Back
                    title="Branch List"
                    onCallback={() => {
                      if (isEdit) {
                        props.navigation.navigate('BranchListDetailScreen', {
                          titleScreen: props.route.params.name,
                          name: props.route.params.name,
                          id: props.route.params.id,
                        });
                      } else {
                        props.navigation.navigate('BranchListScreen');
                      }
                    }}
                  />
                  <Layout.Title
                    title={isEdit ? 'Edit Branch' : 'Create Branch'}>
                    <Button
                      rounded={true}
                      buttonStyle={{ width: 120 }}
                      disabledStyle={{ width: 120 }}
                      disabled={isDisableSubmit(values)}
                      title={
                        <SociollaText
                          type="bold"
                          style={{
                            fontSize: 14,
                            letterSpacing: 0.5,
                            color: Colors.white,
                          }}>
                          {isEdit ? 'UPDATE' : 'SAVE'}
                        </SociollaText>
                      }
                      onPress={handleSubmit}
                    />
                  </Layout.Title>
                </Layout.Wrapper>
              ) : (
                false
              )}
              <Layout.Wrapper rounded bottomSpace={!isWeb ? 70 : 0} isHeight>
                <ScrollView>
                  {page == 1 ? (
                    <Layout.FormWrapper
                      style={{
                        flexDirection: isWeb ? 'row' : 'column',
                        paddingHorizontal: 15,
                        ...(isWeb
                          ? {
                              paddingTop: 20,
                              paddingVertical: 20,
                            }
                          : {
                              paddingTop: 10,
                            }),
                      }}>
                      <View style={{ width: isWeb ? '50%' : '100%' }}>
                        <Layout.FormGroup>
                          <Input
                            label="Branch Name"
                            value={values.name}
                            isRequired={true}
                            hasError={
                              errors.name && touched.name ? errors.name : null
                            }
                            onChangeText={handleChange('name')}
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup>
                          <Input
                            label="Branch Code"
                            value={values.branch_code}
                            isRequired={true}
                            maxLength={2}
                            hasError={
                              errors.branch_code && touched.branch_code
                                ? errors.branch_code
                                : null
                            }
                            onChangeText={handleChange('branch_code')}
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup>
                          <Input
                            label="Branch Head"
                            value={
                              isEdit &&
                              detailData.branch_head &&
                              detailData.branch_head.name
                                ? detailData.branch_head.name
                                : ''
                            }
                            editable={false}
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup>
                          <SociollaSelectBox
                            isTransparent={true}
                            label="Active/Inactive"
                            options={[
                              {
                                label: 'Active',
                                value: true,
                              },
                              {
                                label: 'In Active',
                                value: false,
                              },
                            ]}
                            name="is_active"
                            trackBy="value"
                            labelKey="label"
                            isRequired
                            value={values.is_active}
                            onChange={(val) => {
                              setFieldValue('is_active', val);
                            }}
                          />
                        </Layout.FormGroup>
                      </View>
                      <View style={{ width: isWeb ? '50%' : '100%' }}>
                        <Layout.FormGroup>
                          <SelectBox
                            label="Regional/Area"
                            options={[
                              { name: 'West', value: 'west' },
                              { name: 'East', value: 'east' },
                            ]}
                            value={values.regional}
                            placeholder="Select Regional/Area"
                            trackBy="value"
                            labelKey="name"
                            isRequired={true}
                            hasError={
                              errors.regional && touched.regional
                                ? errors.regional
                                : null
                            }
                            onChange={(value) =>
                              setFieldValue('regional', value)
                            }
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup>
                          <SalesTeamSearch
                            value={values.sales_team}
                            isRequired={true}
                            multiple={true}
                            hasError={
                              errors.sales_team && touched.sales_team
                                ? errors.sales_team
                                : null
                            }
                            customFilter={{
                              $or: [
                                { branch_id: { $exists: false } },
                                { branch_id: null },
                              ],
                            }}
                            onChange={(value) => {
                              if (value && value.length) {
                                handleSalesTeamChange(value);
                                setFieldValue('sales_team', value);
                              } else {
                                handleSalesTeamChange(value, true);
                                setFieldValue('sales_team', []);
                              }
                            }}
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup>
                          <SelectBox
                            label="Warehouse"
                            options={[]}
                            value={warehouse}
                            placeholder="Warehouse"
                            trackBy="id"
                            labelKey="name"
                            isRequired={true}
                            multiple={true}
                            editable={false}
                          />
                        </Layout.FormGroup>
                      </View>
                    </Layout.FormWrapper>
                  ) : (
                    false
                  )}
                  {isWeb || page == 2 ? (
                    <TeamMember data={teamMember} />
                  ) : (
                    false
                  )}
                </ScrollView>
              </Layout.Wrapper>
              {!isWeb ? (
                <View style={buttonFloating}>
                  {page == 1 ? (
                    <Button
                      rounded={true}
                      buttonStyle={{ width: '100%' }}
                      disabledStyle={{ width: '100%' }}
                      disabled={isDisableSubmit(values)}
                      title={
                        <SociollaText
                          type="bold"
                          style={{
                            fontSize: 14,
                            letterSpacing: 0.5,
                            color: Colors.white,
                          }}>
                          NEXT
                        </SociollaText>
                      }
                      onPress={() => setPage(2)}
                    />
                  ) : (
                    <View
                      style={{
                        width: '100%',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}>
                      <View style={{ width: '48%' }}>
                        <Button
                          type="outline-primary"
                          rounded={true}
                          buttonStyle={{ width: '100%' }}
                          title={
                            <SociollaText
                              type="bold"
                              style={{
                                fontSize: 14,
                                letterSpacing: 0.5,
                                color: Colors.blue1,
                              }}>
                              PREV
                            </SociollaText>
                          }
                          onPress={() => setPage(1)}
                        />
                      </View>
                      <View style={{ width: '48%' }}>
                        <Button
                          rounded={true}
                          buttonStyle={{ width: '100%' }}
                          title={
                            <SociollaText
                              type="bold"
                              style={{
                                fontSize: 14,
                                letterSpacing: 0.5,
                                color: Colors.white,
                              }}>
                              {isEdit ? 'UPDATE' : 'SAVE'}
                            </SociollaText>
                          }
                          onPress={(evt) => {
                            evt.persist();
                            setTimeout(() => {
                              validateForm().then((validate) => {
                                handleSubmit(evt);
                                if (validate && Object.keys(validate).length) {
                                  setPage(1);
                                }
                              });
                            }, 100);
                          }}
                        />
                      </View>
                    </View>
                  )}
                </View>
              ) : (
                false
              )}
            </>
          )}
        </Formik>
      </Layout.Container>
    </>
  ) : (
    <Layout.Container>
      <Layout.Wrapper rounded>
        <Layout.Loader />
      </Layout.Wrapper>
    </Layout.Container>
  );
};

export default BranchListFormScreen;
